/*global chrome*/
import './index.css';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Flex,
  Button,
  SimpleSelect,
  IconRefreshSolid,
  IconButton,
  Alert,
} from '@smarterservices/smarter-ui';
import AudioVisualizer from './AudioVisualizer';
import StepInstructions from '../Common/StepInstructions';
import { sendMessageToExtension, parseMediaDeviceError, getOperatingSystem } from '../../helpers/helpers';
import DualPane from '../DualPane';
import { usePaneViewer } from '../PaneViewer';
import Loader from '../Common/Loader';
import { useLogger } from '@ui/shared/components/Logger';
import PageError from '../PageError';
import { useAppStore } from '../../stores/app';

/**
 * ShareMicrophone Component
 * 
 * A component that allows users to select and share their microphone for audio input.
 * Handles device enumeration, selection, and audio stream management.
 * 
 * @param {Object} props - Component props
 * @param {Object} props.stepData - Configuration and content for the current step
 * @param {Function} props.setIsTaskComplete - Callback to mark task as complete
 * @param {Function} props.setStepMetadata - Callback to update step metadata
 * @returns {React.ReactElement} The rendered component
 */
const ShareMicrophone = ({ stepData, setIsTaskComplete, setStepMetadata }) => {
  
  // Audio state management
  const [isSharing, setIsSharing] = useState(false);
  const [isSettingUpShare, setIsSettingUpShare] = useState(false);
  const [audioTrack, setAudioTrack] = useState(null);
  const [loadingAudioTrack, setLoadingAudioTrack] = useState(true);
  const [deviceSoundDetected, setDeviceSoundDetected] = useState(false);
  const [audioInputs, setAudioInputs] = useState([]);
  const [selectedAudioLabel, setSelectedAudioLabel] = useState('');
  const [shareError, setShareError] = useState(null);
  const [audioStreamErrorCode, setAudioStreamErrorCode] = useState('');
  
  // Refs
  const audioTrackRef = useRef(null);
  const streamRef = useRef(null);
  
  // Hooks
  const { onNext } = usePaneViewer();
  const logger = useLogger();
  
  // Constants
  const MICROPHONE_STORAGE_KEY = 'spSelectedAudioLabel';

  /**
   * Fetches and sets up available audio input devices
   * Handles device enumeration and initial device selection
   */
  const getAudioDevices = useCallback(async () => {
    try {
      
      // Check if audio is already published
      const isAlreadyPublished = await checkIfAudioIsPublished();
      if (isAlreadyPublished) {
        handleAlreadyPublished();
        return;
      }
      
      // Get default stream to ensure permissions
      const defaultStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Enumerate and process available devices
      const devices = await navigator.mediaDevices.enumerateDevices();
      const microphones = devices.filter(device => device.kind === 'audioinput');
      
      // Reset sound detection state when refreshing devices
      setDeviceSoundDetected(false);
      setAudioInputs(microphones);
      // Handle device selection
      await handleDeviceSelection(microphones, defaultStream);
      
      // Clean up the default stream
      cleanupStream(defaultStream);
      
    } catch (error) {
      const mediaError = parseMediaDeviceError(error);
      // lets add logging here so we can catch any errors for support and debugging.
      setAudioStreamErrorCode(mediaError.code);
      // now add the query params back to the URL, this way when the browser re-opens it can open to this step...
      useAppStore.getState().restoreLaunchParams();
      logger.logError("SessionUI", 'Error fetching audio devices:', mediaError);
      console.log('Error fetching audio devices:', mediaError);
      setShareError('Failed to access microphone. Please check your permissions.');
    }
  }, [setIsTaskComplete, onNext]);
  
  // Initialize device list on mount
  useEffect(() => {
    getAudioDevices();
    
    // Cleanup function
    return () => {
      if (audioTrackRef.current) {
        audioTrackRef.current.stop();
      }
      if (streamRef.current) {
        cleanupStream(streamRef.current);
      }
    };
  }, []);
  
  /**
   * Checks if audio is already published
   * @returns {Promise<boolean>} True if audio is already published
   */
  const checkIfAudioIsPublished = async () => {
    try {
      return await sendMessageToExtension({
        message: 'sp_background__isTrackPublished',
        trackName: 'audio',
      });
    } catch (error) {
      //TODO: What do we want to do here?
      console.error('Error checking if audio is published:', error);
      return false;
    }
  };
  
  /**
   * Handles the case when audio is already published
   */
  const handleAlreadyPublished = () => {
    setIsSharing(true);
    setIsTaskComplete(true);
    onNext();
  };
  
  /**
   * Handles device selection logic
   * @param {Array} microphones - List of available microphone devices
   * @param {MediaStream} defaultStream - The default media stream
   */
  const handleDeviceSelection = async (microphones, defaultStream) => {
    if (!microphones.length) return;
    
    const previouslySelectedMicrophoneLabel = localStorage.getItem(MICROPHONE_STORAGE_KEY);
    const defaultDeviceId = defaultStream?.getAudioTracks()?.[0]?.getSettings()?.deviceId;
    
    // Try to find the previously selected device
    const selectedDevice = findSelectedDevice(microphones, previouslySelectedMicrophoneLabel, defaultDeviceId);
    
    if (selectedDevice) {
      await handleSelect(selectedDevice.deviceId);
    } else if (microphones.length > 0) {
      // Fallback to first available device if no selection found
      await handleSelect(microphones[0].deviceId);
    }
  };
  
  /**
   * Finds the selected device from available microphones
   * @param {Array} microphones - List of available microphones
   * @param {string} previouslySelectedLabel - Previously selected device label
   * @param {string} defaultDeviceId - Default device ID
   * @returns {Object|null} Selected device or null if not found
   */
  const findSelectedDevice = (microphones, previouslySelectedLabel, defaultDeviceId) => {
    if (previouslySelectedLabel) {
      const device = microphones.find(mic => mic.label === previouslySelectedLabel);
      if (device) {
        setSelectedAudioLabel(device.label);
        return device;
      }
    }
    
    if (defaultDeviceId) {
      const defaultDevice = microphones.find(mic => mic.deviceId === defaultDeviceId);
      if (defaultDevice) {
        setSelectedAudioLabel(defaultDevice.label || 'Default Microphone');
        return defaultDevice;
      }
    }
    
    return null;
  };
  
  /**
   * Cleans up a media stream by stopping all tracks
   * @param {MediaStream} stream - The stream to clean up
   */
  const cleanupStream = (stream) => {
    if (!stream) return;
    stream.getTracks().forEach(track => {
      if (track.readyState === 'live') {
        track.stop();
      }
    });
  };

  /**
   * Handles selection of an audio input device
   * @param {string} deviceId - The ID of the device to select
   */
  const handleSelect = useCallback(async (deviceId) => {
    if (!deviceId) {
      console.error('No device ID provided to handleSelect');
      return;
    }
    
    try {
      // Reset sound detection state
      setDeviceSoundDetected(false);
      
      // Get fresh device list
      const devices = await navigator.mediaDevices.enumerateDevices();
      const microphones = devices.filter(device => device.kind === 'audioinput');
      setAudioInputs(microphones);
      
      // Find the selected device
      const selectedDevice = microphones.find(device => device.deviceId === deviceId);
      if (!selectedDevice) {
        throw new Error(`Device not found: ${deviceId}`);
      }
      
      setSelectedAudioLabel(selectedDevice.label);
      
      // Clean up existing resources
      cleanupAudioResources();
      
      // Set up new audio stream
      await setupAudioStream(deviceId);
      
    } catch (error) {
      console.error('Error in handleSelect:', error);
      setShareError('Failed to access the selected microphone. Please check permissions.');
      setSelectedAudioLabel('Error: Check Permissions');
    }
  }, []);
  
  /**
   * Cleans up audio resources (tracks and streams)
   */
  const cleanupAudioResources = useCallback(() => {
    if (audioTrackRef.current) {
      audioTrackRef.current.stop();
      audioTrackRef.current = null;
    }
    
    if (streamRef.current) {
      cleanupStream(streamRef.current);
      streamRef.current = null;
    }
    
    setAudioTrack(null);
  }, []);
  
  /**
   * Sets up a new audio stream with the specified device
   * @param {string} deviceId - The device ID to use for the stream
   */
  const setupAudioStream = useCallback(async (deviceId) => {
    try {
      
      setLoadingAudioTrack(true);
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { 
          deviceId: { exact: deviceId },
          // Consider adding these constraints for better quality:
          // echoCancellation: true,
          // noiseSuppression: true,
          // autoGainControl: true
        },
      });
      
      const [track] = stream.getAudioTracks();
      if (!track) {
        throw new Error('No audio track found in the stream');
      }
      
      // Set up track ended handler
      track.addEventListener('ended', handleTrackEnded);
      
      // Update refs and state
      audioTrackRef.current = track;
      streamRef.current = stream; // Keep reference to prevent GC
      setAudioTrack(track);
      setLoadingAudioTrack(false);      
    } catch (error) {
      console.error('Error setting up audio stream:', error);
      throw error; // Re-throw to be handled by the caller
    }
  }, []);
  
  /**
   * Handles the 'ended' event on audio tracks
   */
  const handleTrackEnded = useCallback(() => {
    console.log('Audio track ended');
    setShareError('Microphone access was interrupted. Please select your microphone again.');
    cleanupAudioResources();
  }, [cleanupAudioResources]);
  /**
   * Handles the refresh button click
   */
  const handleRefresh = useCallback(() => {
    setShareError(null);
    getAudioDevices();
  }, [getAudioDevices]);

  /**
   * Handles device selection change from the dropdown
   * @param {Object} event - The change event
   * @param {Object} data - The selected option data
   * @param {string} data.id - The device ID
   * @param {string} data.value - The device label
   */
  const handleSelectChange = useCallback((event, { id }) => {
    if (!id) return;
    handleSelect(id);
  }, [handleSelect]);

  /**
   * Handles sharing the selected audio device
   */
  const handleShareAudio = useCallback(async () => {
    if (!selectedAudioLabel) {
      console.warn('No audio input selected');
      return;
    }
    
    setShareError(null);
    setIsSettingUpShare(true);
    
    try {
      // Save the selected microphone
      setStepMetadata({ selectedMicrophone: selectedAudioLabel });
      localStorage.setItem(MICROPHONE_STORAGE_KEY, selectedAudioLabel);

      // Share audio with the extension
      const response = await sendMessageToExtension({
        message: 'sp_background__shareAudio',
        deviceLabel: selectedAudioLabel,
      });

      if (response === null) {
        // No listener on extension, reload to reinitialize
        window.location.reload();
        return;
      }

      setIsSharing(response);

      if (response) {
        // Success - move to next step
        setIsTaskComplete(true);
        onNext();
      } else {
        // Failed to share
        setShareError('Failed to connect microphone. Please try again.');
      }
    } catch (error) {
      console.error('Error while sharing audio:', error);
      setShareError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSettingUpShare(false);
    }
  }, [selectedAudioLabel, setStepMetadata, setIsTaskComplete, onNext]);


  // If there is an error, surface the proper error screen...
    if (audioStreamErrorCode) {
      const errorContent = stepData?.mediaErrors?.[audioStreamErrorCode];

      // get the OS of the user...
      const os = getOperatingSystem(window).toLowerCase();
    
      let errorText = "";
      // Log that the OS was not found in the error content...
      if(!errorContent?.[os]){
        logger.logError("SessionUI", 'Error fetching page error content for OS (microphone):', os, null, {appVersion: window.navigator.appVersion});
        errorText = "There was an error accessing your microphone. Please check your settings, if the problem persists reach out to support.";
      }else{
        errorText = errorContent?.[os];
      }
      return (
        <PageError
          errorText={errorText}
          showRefreshButton={errorContent?.showRefreshButton}
          refreshButtonText={errorContent?.refreshButtonText}
          refreshIntroText={errorContent?.refreshIntroText}
          errorCode={audioStreamErrorCode}
          maxWidth="600px"
        />
      );
    }

  // Render the component
  return (
    <DualPane
      leftPane={
        <StepInstructions instructions={stepData?.body} />
      }
      rightPane={
        <>
        {(isSettingUpShare && loadingAudioTrack) && (
          <Loader text='Configuring microphone...' />
        )} 
        {(loadingAudioTrack && !isSettingUpShare) && (
          <Loader text='Getting audio stream...' />
        )} 
        {(!loadingAudioTrack && !isSettingUpShare) && (
          <Flex direction="column" alignItems="center" width="100%">
            {/* Error Alert */}
            {shareError && (
              <Alert
                variant="error"
                renderCloseButtonLabel="Close"
                margin="small"
                variantScreenReaderLabel="Error, "
                onDismiss={() => setShareError(null)}
              >
                {shareError}
              </Alert>
            )}
            
            {/* Device Selection */}
            <Flex direction="row" alignItems="center" margin="0 0 medium 0" wrap="wrap">
              <SimpleSelect
                renderLabel=""
                value={selectedAudioLabel || 'Select Microphone'}
                onChange={handleSelectChange}
                width="400px"
                isDisabled={isSettingUpShare}
              >
                {audioInputs.length > 0 ? (
                  audioInputs.map((device, index) => (
                    <SimpleSelect.Option
                      key={device.deviceId || `mic-${index}`}
                      id={device.deviceId}
                      value={device.label}
                    >
                      {device.label || `Microphone ${index + 1}`}
                    </SimpleSelect.Option>
                  ))
                ) : (
                  <SimpleSelect.Option id="no-mics" value="no-mics">
                    No microphones available
                  </SimpleSelect.Option>
                )}
              </SimpleSelect>
              
              {/* Audio Visualizer (hidden) */}
              {audioTrack && (
                <AudioVisualizer 
                  showBars={false} 
                  track={audioTrack} 
                  onSoundDetected={() => setDeviceSoundDetected(true)}
                />
              )}
              
              <IconButton
                color="primary"
                margin="0 0 0 small"
                onClick={handleRefresh}
                screenReaderLabel="Refresh microphone list"
                interaction={isSettingUpShare ? 'disabled' : 'enabled'}
              >
                <IconRefreshSolid />
              </IconButton>
            </Flex>
            
            {/* Action Button or Loader */}
            {deviceSoundDetected ? (
              <Button
                color="primary"
                size="medium"
                onClick={handleShareAudio}
                interaction={isSharing || !selectedAudioLabel ? 'disabled' : 'enabled'}
                margin="medium 0 0 0"
              >
                {stepData?.button || 'Share Audio'}
              </Button>
            ) : (
              <Alert 
                variant="info" 
                margin="medium 0 0 0" 
                screenReaderLabel={stepData?.confirmAudioInstructions || 'Please speak to confirm your microphone is working'}
              >
                {stepData?.confirmAudioInstructions || 'Please speak to confirm your microphone is working'}
              </Alert>
            )}
          </Flex>
        )}
        </>
      }
    />
  );
};

// Prop types validation
ShareMicrophone.propTypes = {
  stepData: PropTypes.shape({
    body: PropTypes.string,
    button: PropTypes.string,
    confirmAudioInstructions: PropTypes.string,
  }),
  setIsTaskComplete: PropTypes.func.isRequired,
  setStepMetadata: PropTypes.func.isRequired,
};

export default React.memo(ShareMicrophone);
